import { Fragment, useContext, useState } from "react";
import { useDateFormatter } from "react-aria";
import {
  CalendarGrid as AriaCalendarGrid,
  CalendarGridBody as AriaCalendarGridBody,
  CalendarGridHeader as AriaCalendarGridHeader,
  CalendarHeaderCell as AriaCalendarHeaderCell,
  RangeCalendar as AriaRangeCalendar,
  RangeCalendarContext,
  RangeCalendarStateContext,
  useSlottedContext,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import { NextCalendarControls, PreviousCalendarControls } from "../date-picker/CalendarControls";
import { CalendarCell } from "./Cell";

import type { PropsWithChildren } from "react";
import type {
  RangeCalendarProps as AriaRangeCalendarProps,
  DateValue,
} from "react-aria-components";

const rangeCalendarSlots = tv({
  slots: {
    navigationButton: [
      "cursor-pointer border-none bg-transparent",
      "text-gray-700 hover:text-gray-900",
      "dark:text-gray-300 dark:hover:text-gray-100",
    ],
    calendarHeaderCell: "border-b-4 border-transparent p-0",
    calendarGridBody:
      "[&_td]:p-0 [&_tr:last-of-type]:border-none [&_tr]:border-b-4 [&_tr]:border-transparent",
  },
});

export const RangeCalendarContextProvider = ({ children }: PropsWithChildren) => {
  const [value, onChange] = useState<{ start: DateValue; end: DateValue } | null>(null);
  const [focusedValue, onFocusChange] = useState<DateValue | undefined>();

  return (
    <RangeCalendarContext.Provider value={{ value, onChange, focusedValue, onFocusChange }}>
      {children}
    </RangeCalendarContext.Provider>
  );
};

const RangeCalendarTitle = ({ part }: { part: "start" | "end" }) => {
  const context = useContext(RangeCalendarStateContext);

  if (!context) {
    throw new Error("<RangeCalendarTitle /> must be used within a <RangeCalendar /> component.");
  }

  const formatter = useDateFormatter({
    month: "long",
    year: "numeric",
    calendar: context.visibleRange.start.calendar.identifier,
    timeZone: context.timeZone,
  });

  return part === "start"
    ? formatter.format(context.visibleRange.start.toDate(context.timeZone))
    : formatter.format(context.visibleRange.end.toDate(context.timeZone));
};

interface RangeCalendarProps extends AriaRangeCalendarProps<DateValue> {
  /** The dates to highlight. */
  highlightedDates?: DateValue[];
  /** The date presets to display. */
  presets?: Record<string, { label: string; value: { start: DateValue; end: DateValue } }>;
}

export const RangeCalendar = ({ ...props }: RangeCalendarProps) => {
  const context = useSlottedContext(RangeCalendarContext);
  const slots = rangeCalendarSlots();

  const ContextWrapper = context ? Fragment : RangeCalendarContextProvider;

  return (
    <ContextWrapper>
      <AriaRangeCalendar
        className="flex items-stretch"
        visibleDuration={{
          months: 2,
        }}
        pageBehavior="single"
        {...props}
      >
        {({ state: calendarState }) => (
          <>
            <div className="flex flex-col gap-3 p-3">
              <header className="relative flex items-center justify-between md:justify-start">
                <PreviousCalendarControls calendarState={calendarState} />
                <h2 className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-sm font-semibold text-primary">
                  <RangeCalendarTitle part="start" />
                </h2>
              </header>

              <AriaCalendarGrid weekdayStyle="short" className="w-max">
                <AriaCalendarGridHeader>
                  {day => (
                    <AriaCalendarHeaderCell className={slots.calendarHeaderCell()}>
                      <div className="flex size-10 items-center justify-center text-sm font-semibold text-primary">
                        {day.slice(0, 2)}
                      </div>
                    </AriaCalendarHeaderCell>
                  )}
                </AriaCalendarGridHeader>
                <AriaCalendarGridBody className={slots.calendarGridBody()}>
                  {date => <CalendarCell date={date} />}
                </AriaCalendarGridBody>
              </AriaCalendarGrid>
            </div>
            {/* Vertical separator that stretches to the tallest column */}
            <div aria-hidden className="w-px self-stretch bg-gray-200 dark:bg-gray-700" />

            <div className="relative flex h-full flex-col gap-3 p-3">
              <header className="relative flex items-center justify-end">
                <h2 className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-sm font-semibold text-primary">
                  <RangeCalendarTitle part="end" />
                </h2>

                <NextCalendarControls calendarState={calendarState} />
              </header>

              <AriaCalendarGrid weekdayStyle="short" offset={{ months: 1 }} className="w-max">
                <AriaCalendarGridHeader>
                  {day => (
                    <AriaCalendarHeaderCell className={slots.calendarHeaderCell()}>
                      <div className="flex size-10 items-center justify-center text-sm font-semibold text-primary">
                        {day.slice(0, 2)}
                      </div>
                    </AriaCalendarHeaderCell>
                  )}
                </AriaCalendarGridHeader>
                <AriaCalendarGridBody className={slots.calendarGridBody()}>
                  {date => <CalendarCell date={date} />}
                </AriaCalendarGridBody>
              </AriaCalendarGrid>
            </div>
          </>
        )}
      </AriaRangeCalendar>
    </ContextWrapper>
  );
};
